import { z } from 'zod';

/**
 * Schemas for workspace (tenant) onboarding (ARCHITECTURE_PLAN §9:
 * `POST /api/tenants` — create workspace; creator becomes owner). Single
 * source of truth shared by Worker and SPA (§3).
 *
 * The route handler is a later Phase 1 slice; these schemas are its contract.
 */

export const TenantVerticalSchema = z.enum(['hvac', 'plumbing', 'electrical', 'roofing']);
export type TenantVertical = z.infer<typeof TenantVerticalSchema>;

export const TenantStatusSchema = z.enum(['active', 'trial', 'suspended', 'cancelled']);
export type TenantStatus = z.infer<typeof TenantStatusSchema>;

export const TenantPlanSchema = z.enum(['trial', 'solo', 'business', 'growth']);
export type TenantPlan = z.infer<typeof TenantPlanSchema>;

/**
 * URL path segment identifying a workspace (`/w/:slug`, `/api/w/:slug`).
 * Lowercase DNS-label style: starts and ends alphanumeric, hyphens inside.
 * Slugs are how tenants are addressed publicly; they are globally unique
 * (migration 0001 `tenants.slug UNIQUE`).
 */
export const TenantSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(
    /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/,
    'slug must be 2-64 chars of lowercase letters, digits, and single hyphens'
  );

/** IANA timezone names only (drives availability + usage-period math later). */
const IanaTimezoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .refine(
    (tz) => {
      try {
        // Throws RangeError for unknown timezones.
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
      } catch {
        return false;
      }
    },
    { message: 'timezone must be a valid IANA name (e.g. America/New_York)' }
  );

/** `POST /api/tenants` request body (create-workspace onboarding). */
export const CreateWorkspaceBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  slug: TenantSlugSchema,
  vertical: TenantVerticalSchema.default('hvac'),
  timezone: IanaTimezoneSchema.default('America/New_York'),
});
export type CreateWorkspaceBody = z.infer<typeof CreateWorkspaceBodySchema>;

/** Workspace summary returned to the SPA. */
export const TenantSummarySchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  vertical: TenantVerticalSchema,
  status: TenantStatusSchema,
  plan: TenantPlanSchema,
  timezone: z.string(),
});
export type TenantSummary = z.infer<typeof TenantSummarySchema>;

/** `POST /api/tenants` response. */
export const CreateWorkspaceResponseSchema = z.object({
  tenant: TenantSummarySchema,
});
export type CreateWorkspaceResponse = z.infer<typeof CreateWorkspaceResponseSchema>;
